import { Badge, Box, Tab, Tabs, Typography } from "@mui/material";
import { TabPanel } from "components/TabPanel";
import { TabsContextProvider } from "components/tabs/context";
import { useTabsContext } from "components/tabs/hooks";
import { Fragment, useCallback, useMemo } from "react";
import { useGetDocuments } from "core/documents/hooks";
import { toast } from "react-toastify";
import { AccessTime, CancelOutlined, CheckCircleOutline, PanoramaFishEye, WarningAmberOutlined } from "@mui/icons-material";
import DocumentForm from "../forms/document.form";
import FreeFormDocuments from "../forms/free_form_documents.form";

interface Props {
    tempWorker: any;
}

const baseMandatoryDocuments = [
  {
    label: "Carte d'identité",
    key: 'identity',
  },
  {
    label: 'Justif. domicile',
    key: 'residency_proof',
  },
  {
    label: 'RIB',
    key: 'bank_details',
  },
  {
    label: 'Carte vitale',
    key: 'ssn_card',
  },
  {
    label: 'CV',
    key: 'resume',
  },
];

const driverLicenseDocument = {
  label: 'Permis de conduire',
  key: 'driver_license',
};

const medicalVisitDocument = {
  label: 'Visite médicale',
  key: 'medical_visit',
};

const getMandatoryDocuments = (tempWorker: any) => {
  let docs = [...baseMandatoryDocuments];
  if (tempWorker?.driver_license === true) {
    docs = [...docs, driverLicenseDocument];
  }
  if (tempWorker?.has_medical_visit === true) {
    docs = [...docs, medicalVisitDocument];
  }
  return docs;
};

const DocumentTabSubPartial = ({tempWorker}: Props) => {
    // Custom Hooks
  const {tabValue, changeTab} = useTabsContext();
  const onError = useCallback(() => toast.error("une erreur est survenue"), []);
  const {data: documentsData } = useGetDocuments(tempWorker.id, { onError });
  const mandatoryDocuments = useMemo(
    () => getMandatoryDocuments(tempWorker),
    [tempWorker]
  );

  const documentOfKind = useCallback(
    (key: string) => documentsData?.documents?.find((doc: any) => doc.key === key),
    [documentsData]
  );

  const statusIcon = (key: string) => {
    const doc = documentOfKind(key);
    switch (doc?.validation_state) {
      case 'pending':
        return <AccessTime color="warning" sx={{ fontSize: '1.25rem', mt: '0.5rem' }} />;
      case 'approved':
        return <CheckCircleOutline color="success" sx={{ fontSize: '1.25rem', mt: '0.5rem' }} />;
      case 'rejected':
        return <CancelOutlined color="error" sx={{ fontSize: '1.25rem', mt: '0.5rem' }} />;
      default:
        return <PanoramaFishEye sx={{ fontSize: '1.25rem', mt: '0.5rem', color: 'grey.300' }} />;
    }
  };

  const isModifiedAfterSync = useMemo(() => {
    const currentDocKey = mandatoryDocuments[tabValue]?.key;
    const currentDoc = currentDocKey ? documentOfKind(currentDocKey) : null;
    return (
      currentDoc &&
      tempWorker.synced_at &&
      currentDoc.uploaded_by === 'user' &&
      new Date(currentDoc.created_at) > new Date(tempWorker.synced_at)
    );
  }, [documentOfKind, mandatoryDocuments, tabValue, tempWorker.synced_at]);

  return (
    <Fragment>
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Tabs
          value={tabValue}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          indicatorColor='primary'
          TabIndicatorProps={{
            sx: {
              backgroundColor: 'white',
              height: '2.5rem',
              borderRadius: '0.25rem',
              zIndex: 0,
            },
          }}
          onChange={changeTab}
          sx={{
            backgroundColor: 'primary.50',
            borderRadius: '0.5rem',
            padding: '0.25rem',
            minHeight: 'unset',
            '& .MuiTabs-scrollButtons': { width: 28, borderRadius: '0.375rem', '&.Mui-disabled': { opacity: 0.25 } },
            "& .MuiTabs-flexContainer button": {
              zIndex: 2,
              padding: '0.5rem 1rem',
              borderRadius: '0.25rem',
              margin: '0 0.25rem',
              minHeight: 'unset',
              height: '2.5rem',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              '&:hover': {
                backgroundColor: 'grey.100',
              }
            },
            '& button.Mui-selected': {
              color: 'text.primary',
              border: '1px solid #e2e2e2',
              '&:hover': {
                backgroundColor: 'white',
              }
            }
          }}
          aria-label="Tab document candidat"
        >
          {mandatoryDocuments.map((doc) => {
            const document = documentOfKind(doc.key);
            const showBadge = document && document.validation_state === 'pending' && ['completed', 'failed'].includes(document.ocr_job_status);

            return (
              <Tab
                icon={statusIcon(doc.key)}
                key={doc.key}
                label={
                  <Badge
                    color="error"
                    badgeContent=""
                    variant="dot"
                    invisible={!showBadge}
                  >
                    {doc.label}
                  </Badge>
                }
                disableRipple
                sx={{ textTransform: 'none' }}
              />
            );
          })}
          <Tab label="Complémentaires" disableRipple sx={{ textTransform: 'none' }} />
        </Tabs>
      </Box>
      {isModifiedAfterSync && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main', px: '1rem', pt: '0.75rem' }}>
          <WarningAmberOutlined fontSize='small' />
          <Typography variant='body2'>Ce document a été modifié par l'utilisateur après sa synchronisation</Typography>
        </Box>
      )}
      <Box sx={{ p: '1rem 0' }}>
        {mandatoryDocuments.map((doc, index) => (
          <TabPanel value={tabValue} index={index} key={index}>
            <DocumentForm
              tempWorker={tempWorker}
              document={documentOfKind(doc.key)}
              kind={doc.key}
              label={doc.label}
            />
          </TabPanel>
        ))}
        <TabPanel value={tabValue} index={mandatoryDocuments.length} key={mandatoryDocuments.length}>
          <FreeFormDocuments tempWorker={tempWorker} />
        </TabPanel>
      </Box>
    </Fragment>
  );
};

const DocumentTabPartial = ({tempWorker}: Props) => {
  return (
    <Box sx={{ width: '100%' }}>
      <TabsContextProvider>
        <DocumentTabSubPartial tempWorker={tempWorker} />
      </TabsContextProvider>
    </Box>
  );
};

export { DocumentTabPartial, getMandatoryDocuments };
